/* Mobile PDP gallery pagination dots - vanilla custom element, no carousel
 * library. Reads the real [data-slide] elements already in the DOM (the
 * same nodes assets/product-colour-gallery.js filters via data-colour-hidden)
 * so there is exactly one media list on the page; this file never fetches or
 * duplicates an image. Active-dot state is synced from the native scroll
 * position of .product__slides (CSS scroll-snap does the actual swiping),
 * and dots are rebuilt whenever the selected colour changes so the count and
 * active state always match the currently visible media - never stale.
 */
(function () {
  'use strict';

  if (customElements.get('product-gallery-dots')) return;

  var MOBILE_QUERY = '(max-width: 749px)';
  var WINDOW_SPAN = 5; // visible dots around the active one once count > 7

  customElements.define(
    'product-gallery-dots',
    class ProductGalleryDots extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('product-info') || document;
        this.track = this.root.querySelector('[data-product-media-list]');
        this.labelTemplate = this.dataset.labelTemplate || '';
        this.mq = window.matchMedia(MOBILE_QUERY);

        if (!this.track) return;

        this.onScroll = this.debounce(this.onScroll.bind(this), 100);
        this.onMediaSelect = this.onMediaSelect.bind(this);
        this.onMqChange = this.onMqChange.bind(this);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.root.addEventListener('theme:media:select', this.onMediaSelect);
        this.addMqListener(this.mq, this.onMqChange);

        this.build();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        this.root.removeEventListener('theme:media:select', this.onMediaSelect);
        this.removeMqListener(this.mq, this.onMqChange);
      }

      addMqListener(mq, fn) {
        if (mq.addEventListener) mq.addEventListener('change', fn);
        else if (mq.addListener) mq.addListener(fn);
      }

      removeMqListener(mq, fn) {
        if (mq.removeEventListener) mq.removeEventListener('change', fn);
        else if (mq.removeListener) mq.removeListener(fn);
      }

      debounce(fn, wait) {
        var timer;
        return function () {
          var args = arguments;
          clearTimeout(timer);
          timer = setTimeout(function () {
            fn.apply(null, args);
          }, wait);
        };
      }

      visibleSlides() {
        return Array.prototype.slice
          .call(this.track.querySelectorAll('[data-slide]'))
          .filter(function (slide) {
            return !slide.hasAttribute('data-colour-hidden');
          });
      }

      label(index) {
        return this.labelTemplate.replace('PRODUCT_GALLERY_DOT_N', String(index + 1));
      }

      build() {
        var slides = this.visibleSlides();
        this.textContent = '';
        this.dots = null;
        this.slides = null;

        if (!this.mq.matches || slides.length < 2) {
          this.hidden = true;
          return;
        }

        this.hidden = false;
        this.slides = slides;

        var list = document.createElement('div');
        list.className = 'product__gallery-dots__list';

        var self = this;
        slides.forEach(function (slide, index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product__gallery-dots__dot';
          dot.setAttribute('aria-label', self.label(index));
          dot.dataset.dotIndex = String(index);
          dot.addEventListener('click', self.onDotClick.bind(self));
          list.appendChild(dot);
        });

        this.appendChild(list);
        this.dots = Array.prototype.slice.call(list.children);
        this.windowed = this.dots.length > 7;
        this.classList.toggle('product__gallery-dots--windowed', this.windowed);
        this.setActive(0);
      }

      onDotClick(event) {
        var index = parseInt(event.currentTarget.dataset.dotIndex, 10) || 0;
        var slide = this.slides && this.slides[index];
        if (!slide || !this.track) return;
        this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        this.setActive(index);
      }

      onScroll() {
        if (!this.slides || !this.slides.length) return;
        var trackLeft = this.track.scrollLeft;
        var closest = 0;
        var closestDistance = Infinity;
        this.slides.forEach(function (slide, index) {
          var distance = Math.abs(slide.offsetLeft - trackLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = index;
          }
        });
        this.setActive(closest);
      }

      setActive(index) {
        if (!this.dots) return;
        this.activeIndex = index;
        this.dots.forEach(function (dot, i) {
          var active = i === index;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        if (this.windowed) this.updateWindow();
      }

      updateWindow() {
        // Compact indicator for >7 media: keep only a small window of dots
        // around the active one in the tab/visual order, so the row never
        // grows wider than the phone regardless of how many media exist.
        var total = this.dots.length;
        var start = Math.max(0, Math.min(this.activeIndex - Math.floor(WINDOW_SPAN / 2), total - WINDOW_SPAN));
        this.dots.forEach(function (dot, i) {
          dot.hidden = i < start || i >= start + WINDOW_SPAN;
        });
      }

      onMediaSelect() {
        // Colour change: reset the mobile swipe track to the new colour's
        // first image before rebuilding dots, so there is never a
        // wrong-colour frame or a dot count/active-state mismatch. Desktop
        // already gets its own reset-to-top in product-colour-gallery.js;
        // this only runs the mobile horizontal-scroll equivalent.
        if (this.mq.matches && this.track) this.track.scrollLeft = 0;
        this.build();
      }

      onMqChange() {
        this.build();
      }
    }
  );
})();
